/**
 * WhatsAppWidget — Floating WhatsApp contact button (localized, 16 languages)
 * Positioned bottom-right, uses centralized BRAND config for phone number
 */
import { useState, forwardRef } from 'react';
import { MessageCircle, X } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContextNew';
import BRAND from '@/config/brand';

const WA: Record<string, { title: string; desc: string; cta: string; prefill: string }> = {
  en: { title: 'Chat with us', desc: 'We typically respond within 2 hours.', cta: 'Open WhatsApp', prefill: 'Hi! I\'m interested in your BPO/Digital services.' },
  fr: { title: 'Discutez avec nous', desc: 'Réponse en moins de 2 heures.', cta: 'Ouvrir WhatsApp', prefill: 'Bonjour ! Je suis intéressé par vos services BPO/Digital.' },
  es: { title: 'Chatea con nosotros', desc: 'Respondemos en menos de 2 horas.', cta: 'Abrir WhatsApp', prefill: '¡Hola! Me interesan sus servicios BPO/Digital.' },
  ar: { title: 'تحدث معنا', desc: 'نرد عادة خلال ساعتين.', cta: 'فتح واتساب', prefill: 'مرحباً! أنا مهتم بخدمات BPO/Digital.' },
  de: { title: 'Chatten Sie mit uns', desc: 'Antwort innerhalb von 2 Stunden.', cta: 'WhatsApp öffnen', prefill: 'Hallo! Ich interessiere mich für Ihre BPO/Digital-Dienste.' },
  it: { title: 'Chatta con noi', desc: 'Rispondiamo entro 2 ore.', cta: 'Apri WhatsApp', prefill: 'Ciao! Sono interessato ai vostri servizi BPO/Digital.' },
  pt: { title: 'Fale conosco', desc: 'Respondemos em até 2 horas.', cta: 'Abrir WhatsApp', prefill: 'Olá! Estou interessado nos seus serviços BPO/Digital.' },
  nl: { title: 'Chat met ons', desc: 'Antwoord binnen 2 uur.', cta: 'WhatsApp openen', prefill: 'Hallo! Ik ben geïnteresseerd in uw BPO/Digital-diensten.' },
  pl: { title: 'Porozmawiaj z nami', desc: 'Odpowiadamy w ciągu 2 godzin.', cta: 'Otwórz WhatsApp', prefill: 'Cześć! Interesuję się Waszymi usługami BPO/Digital.' },
  tr: { title: 'Bizimle sohbet edin', desc: '2 saat içinde yanıt veriyoruz.', cta: 'WhatsApp\'ı aç', prefill: 'Merhaba! BPO/Digital hizmetlerinizle ilgileniyorum.' },
  ru: { title: 'Напишите нам', desc: 'Отвечаем в течение 2 часов.', cta: 'Открыть WhatsApp', prefill: 'Здравствуйте! Интересуют ваши BPO/Digital услуги.' },
  zh: { title: '联系我们', desc: '通常2小时内回复。', cta: '打开WhatsApp', prefill: '您好！我对您的BPO/数字服务感兴趣。' },
  ja: { title: 'チャットする', desc: '通常2時間以内に返信します。', cta: 'WhatsAppを開く', prefill: 'こんにちは！BPO/デジタルサービスに興味があります。' },
  ko: { title: '채팅하기', desc: '보통 2시간 내에 답변합니다.', cta: 'WhatsApp 열기', prefill: '안녕하세요! BPO/디지털 서비스에 관심이 있습니다.' },
  sv: { title: 'Chatta med oss', desc: 'Vi svarar inom 2 timmar.', cta: 'Öppna WhatsApp', prefill: 'Hej! Jag är intresserad av era BPO/Digital-tjänster.' },
  da: { title: 'Chat med os', desc: 'Vi svarer inden for 2 timer.', cta: 'Åbn WhatsApp', prefill: 'Hej! Jeg er interesseret i jeres BPO/Digital-tjenester.' },
};

export const WhatsAppWidget = forwardRef<HTMLDivElement, object>(function WhatsAppWidget(_, ref) {
  const [isOpen, setIsOpen] = useState(false);
  const { language } = useLanguage();

  const t = WA[language] || WA.en;
  const waNumber = BRAND.phone.whatsapp.replace(/[\s+]/g, '');
  const waLink = `https://wa.me/${waNumber}?text=${encodeURIComponent(t.prefill)}`;

  return (
    <div ref={ref} className="fixed bottom-24 md:bottom-16 right-4 z-40">
      {isOpen && (
        <div className="absolute bottom-16 right-0 bg-card rounded-xl shadow-xl border border-border p-4 w-72 animate-in fade-in slide-in-from-bottom-4 duration-300">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-foreground">{t.title}</h3>
            <button onClick={() => setIsOpen(false)} className="text-muted-foreground hover:text-foreground">
              <X className="w-4 h-4" />
            </button>
          </div>
          <p className="text-sm text-muted-foreground mb-4">{t.desc}</p>
          <a
            href={waLink}
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full bg-[#25D366] text-white font-semibold py-2.5 rounded-lg text-center hover:bg-[#22c55e] transition-colors"
          >
            {t.cta}
          </a>
        </div>
      )}

      <button
        onClick={() => setIsOpen(!isOpen)}
        className="bg-[#25D366] hover:bg-[#22c55e] text-white rounded-full p-3.5 shadow-lg transition-all duration-300 hover:scale-110 flex items-center justify-center"
        aria-label={t.title}
      >
        <MessageCircle className="w-6 h-6" />
      </button>
    </div>
  );
});

WhatsAppWidget.displayName = 'WhatsAppWidget';
